import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Obligaciones";

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarObligaciones = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [obligaciones, setObligaciones] = useState([]);
  const [loading, setLoading] = useState(false);

  const authConfig = () => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloObligaciones = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === normalizarTexto(MODULO);
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloObligaciones = useMemo(() => getModuloObligaciones(), []);

  const puedeLeer =
    moduloObligaciones?.puedeLeer === true ||
    moduloObligaciones?.leer === true ||
    moduloObligaciones?.read === true;

  const puedeCrear =
    puedeLeer &&
    (moduloObligaciones?.puedeCrear === true ||
      moduloObligaciones?.crear === true ||
      moduloObligaciones?.create === true);

  const puedeActualizar =
    puedeLeer &&
    (moduloObligaciones?.puedeActualizar === true ||
      moduloObligaciones?.actualizar === true ||
      moduloObligaciones?.editar === true ||
      moduloObligaciones?.update === true);

  const puedeEliminar =
    puedeLeer &&
    (moduloObligaciones?.puedeEliminar === true ||
      moduloObligaciones?.eliminar === true ||
      moduloObligaciones?.delete === true);

  const deleteObservacion = useCallback(
    async (idObligacion) => {
      if (!puedeEliminar) return;

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "Esta obligación será eliminada permanentemente",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        const { data } = await axios.delete(
          `/api/obligaciones/delete/${idObligacion}`,
          authConfig()
        );

        setObligaciones((prev) =>
          prev.filter((ob) => ob._id !== idObligacion)
        );

        Swal.fire({
          icon: "success",
          title: data.message || "Obligación eliminada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar la obligación:", error);

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la obligación",
          "error"
        );
      }
    },
    [puedeEliminar]
  );

  const getData = useCallback(async () => {
    if (!puedeLeer || !id) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `/api/obligaciones/contrato/${id}`,
        authConfig()
      );

      if (response.data.ok) {
        const data = (response.data.obligaciones || []).map((item, index) => ({
          numero: index + 1,
          observacion: item.observacion,
          _id: item._id,

          funciones: (
            <>
              {/* ESCRITORIO */}
              <div className="d-none d-md-flex gap-2 justify-content-end align-items-center flex-nowrap">
                {puedeActualizar && (
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    title="Editar obligación"
                    type="button"
                    onClick={() =>
                      navigate(`/instructor/obligaciones/editar/${item._id}`, {
                        state: {
                          obligacion: item,
                          contratoId: id,
                        },
                      })
                    }
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {puedeEliminar && (
                  <button
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    title="Eliminar obligación"
                    type="button"
                    onClick={() => deleteObservacion(item._id)}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                )}
              </div>

              {/* RESPONSIVE */}
              <div className="d-flex d-md-none gap-2 flex-nowrap">
                {puedeActualizar && (
                  <button
                    className="btn btn-outline-warning shadow-sm btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                    title="Editar obligación"
                    type="button"
                    onClick={() =>
                      navigate(`/instructor/obligaciones/editar/${item._id}`, {
                        state: {
                          obligacion: item,
                          contratoId: id,
                        },
                      })
                    }
                  >
                    <i className="fa-solid fa-pen-to-square mb-1"></i>
                    <small>Editar</small>
                  </button>
                )}

                {puedeEliminar && (
                  <button
                    className="btn btn-outline-danger shadow-sm btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                    title="Eliminar obligación"
                    type="button"
                    onClick={() => deleteObservacion(item._id)}
                  >
                    <i className="fa-solid fa-trash mb-1"></i>
                    <small>Eliminar</small>
                  </button>
                )}
              </div>
            </>
          ),
        }));

        setObligaciones(data);
      } else {
        setObligaciones([]);
      }
    } catch (error) {
      console.error("Error al cargar las obligaciones:", error);
      setObligaciones([]);
    } finally {
      setLoading(false);
    }
  }, [
    id,
    puedeLeer,
    puedeActualizar,
    puedeEliminar,
    deleteObservacion,
    navigate,
  ]);

  useEffect(() => {
    if (puedeLeer && id) {
      getData();
    }
  }, [puedeLeer, id, getData]);

  const columns = useMemo(
    () => [
      { Header: "N°", accessor: "numero" },
      {
        Header: "Obligación",
        accessor: "observacion",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!puedeLeer) {
    return null;
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Obligaciones contractuales
                </h5>

                <small className="text-secondary">
                  Control y gestión de obligaciones asociadas al contrato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary flex-fill"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark flex-fill"
                    type="button"
                    onClick={() =>
                      navigate(`/instructor/obligaciones/nuevo/${id}`)
                    }
                    title="Nueva obligación"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando obligaciones…</div>
              </div>
            ) : Array.isArray(obligaciones) && obligaciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={obligaciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📑</div>

                <div className="fw-semibold">
                  No hay obligaciones registradas
                </div>

                <div className="small mb-3">
                  No se encontraron obligaciones para este contrato.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(`/instructor/obligaciones/nuevo/${id}`)
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear obligación
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarObligaciones;